// Browser-side TVirt client: same-origin baseUrl (the /api/v6 rewrite proxies to the
// backend) with the bearer token read from the session cookie at call time.

import { TVirtApi } from 'trantor-tvirt-api-js'

import { getSession } from '@/utils/session'
import { notifySessionExpired } from '@/libs/sessionExpiry'

// Requests whose 401 is a LEGITIMATE form answer — a wrong password typed into a
// form — not an expired session: the login itself and the sudo elevation prompt.
// Everything else answering 401 means the session is gone.
const credentialProbes = [
  { method: 'POST', path: '/api/v6/auth/sessions' },
  { method: 'POST', path: '/api/v6/auth/sudo' }
]

function isCredentialProbe(input: RequestInfo | URL, init?: RequestInit) {
  const method = (input instanceof Request ? input.method : (init?.method ?? 'GET')).toUpperCase()

  const path =
    input instanceof Request
      ? new URL(input.url).pathname
      : new URL(String(input), window.location.origin).pathname

  return credentialProbes.some(probe => probe.method === method && probe.path === path)
}

// The global 401 interceptor (round #101): EVERY response of the browser client
// passes here — an expired or revoked session surfaces as 401 on any call, so the
// clearSession + /login handling lives in ONE place instead of every view's catch.
// The error still propagates to the caller (views keep their non-401 handling);
// navigation is delegated to SessionExpiredRedirect via the session-expiry event.
// Never fires on /login: the page must not loop, and its 401s are form feedback.
export const interceptingFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init)

  if (response.status === 401 && window.location.pathname !== '/login' && !isCredentialProbe(input, init)) {
    notifySessionExpired()
  }

  return response
}

export function apiClient() {
  const session = getSession()

  return TVirtApi.createClientV6({
    baseUrl: '',
    fetch: interceptingFetch,
    ...(session ? { token: session.token } : {})
  })
}

// A client bound to an explicit token — for the pre-cookie window of the login flow
// (the forced mustChangePassword change happens BEFORE the session cookie exists).
// No interceptor: there is no session to expire, and the flow lives on /login.
export function apiClientWithToken(token: string) {
  return TVirtApi.createClientV6({ baseUrl: '', token })
}
